import { Component, DestroyRef, ElementRef, inject, NgZone, ViewChild } from '@angular/core';
import { AsyncPipe, NgOptimizedImage } from '@angular/common';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatRecycleRows,
  MatRow,
  MatRowDef,
  MatTable
} from '@angular/material/table';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { ContactColumn } from './models/contacts-columns';
import { catchError, filter, NEVER, of, switchMap, tap } from 'rxjs';
import { ContactsService } from './services/contacts.service';
import { UserService } from '../../../../services/user.service';
import { Contact, ContactsAllResponse, ContactsFilter, ContactType } from './models/contacts';
import { matchesContactFilter } from './contacts-filter.util';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IsActiveComponent } from './components/is-active/is-active.component';
import { ContactTypeComponent } from './components/contact-type/contact-type.component';
import { PhoneCellComponent } from './components/phone-cell/phone-cell.component';
import { ContactFilters } from './components/contact-filters/contact-filters.component';
import { AvatarComponent } from '../../../../components/avatar/avatar.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { openConfirmationDialog } from '../../../../components/confirmation-popup/confirmation-popup';
import { AddPersonContactComponent } from './components/add-person-contact/add-person-contact.component';
import {
  AddDepartmentContactComponent,
  AddDepartmentContactData,
  AddDepartmentContactResult
} from './components/add-department-contact/add-department-contact.component';
import { EditContactComponent, EditContactDialogData } from './components/edit-contact/edit-contact.component';
import { PageHeaderComponent } from '../../../../components/page-header/page-header.component';
import { AdminTabsComponent } from '../admin-tabs/admin-tabs.component';
import { OrganisationsService } from '../organisations/services/organisations.service';
import { ContactsVirtualScrollDataSource, CONTACT_ROW_HEIGHT } from './services/contacts-virtual-scroll.data-source';
import { EllipsisTooltipDirective } from '../../../../directives/ellipsis-tooltip.directive';
import { SupportContactLookupsService } from './components/add-department-contact/services/support-contact-lookups.service';
import { TransportModeIconPipe } from '../../../../pipes/transport-mode-icon.pipe';
import { ActionButtonsSkeletonComponent } from '../users/components/action-buttons-skeleton/action-buttons-skeleton.component';
import { TableSkeletonComponent } from '../users/components/table-skeleton/table-skeleton.component';

const displayNameCollator = new Intl.Collator(undefined, { sensitivity: 'base' });

// Shared dialog chrome for all three contact modals — white surface, hard corners, the spec's
// shadow and backdrop. The rules live in styles/contact-dialog.scss, which has to be global
// because the CDK renders the surface and backdrop outside each component's own view.
const CONTACT_DIALOG_CHROME = { panelClass: 'contact-dialog-panel', backdropClass: 'contact-dialog-backdrop' };

const AVATAR_COLORS_BY_TYPE: Record<ContactType, { background: string; text: string }> = {
  Department: { background: 'var(--sgl-avatar-department)', text: 'var(--sgl-avatar-department-text)' },
  Person: { background: 'var(--sgl-avatar-person)', text: 'var(--sgl-avatar-person-text)' }
};

@Component({
  selector: 'app-contacts',
  standalone: true,
  templateUrl: './contacts.component.html',
  imports: [
    AsyncPipe,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatIcon,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatProgressSpinner,
    MatRecycleRows,
    MatRow,
    MatRowDef,
    MatSort,
    MatSortHeader,
    MatTable,
    ScrollingModule,
    IsActiveComponent,
    ContactTypeComponent,
    PhoneCellComponent,
    ContactFilters,
    AvatarComponent,
    PageHeaderComponent,
    AdminTabsComponent,
    MatButton,
    EllipsisTooltipDirective,
    NgOptimizedImage,
    TransportModeIconPipe,
    ActionButtonsSkeletonComponent,
    TableSkeletonComponent
  ],
  styleUrl: './contacts.component.scss'
})
export class ContactsComponent {
  @ViewChild(MatSort) matSort!: MatSort;
  @ViewChild('headerRow') headerRow!: ElementRef<HTMLElement>;

  @ViewChild(CdkVirtualScrollViewport)
  set virtualScrollViewport(viewport: CdkVirtualScrollViewport | undefined) {
    if (viewport && !this.viewport) {
      this.viewport = viewport;
      this.dataSource.attachViewport(viewport);
      queueMicrotask(() => viewport.checkViewportSize());
    }
  }

  columnsToDisplay: ContactColumn[] = [
    { key: 'displayName', label: 'Name', filterable: true },
    { key: 'email', label: 'Email', filterable: true },
    { key: 'jobTitle', label: 'Job title', filterable: true },
    { key: 'phone', label: 'Phone', filterable: false },
    { key: 'type', label: 'Type', filterable: false }
  ];
  columnsToDisplayMap = new Map(this.columnsToDisplay.map(column => [column.key, column.label]));
  displayedColumns: string[] = [...this.columnsToDisplay.map(col => col.key), 'actions'];
  isUserAdmin$ = of(false);
  isLoading = true;
  isTableInitiating = true;
  hasError = false;
  totalCount = 0;
  // Badge counts for app-admin-tabs. allContactsCount is the true unfiltered total (totalCount
  // above tracks the currently-filtered/sorted set instead — see applyFilterAndSort);
  // organisationsCount has no equivalent already fetched here, so it's a dedicated lightweight
  // call purely for the badge.
  allContactsCount: number | null = null;
  organisationsCount: number | null = null;
  readonly rowHeight = CONTACT_ROW_HEIGHT;
  readonly dataSource = new ContactsVirtualScrollDataSource(inject(NgZone));

  private contactsService = inject(ContactsService);
  private organisationsService = inject(OrganisationsService);
  private userService = inject(UserService);
  private lookupsService = inject(SupportContactLookupsService);
  private destroyRef = inject(DestroyRef);
  private matDialog = inject(MatDialog);
  private matSnackBar = inject(MatSnackBar);

  // Keyed by Product id, for productName() below — the grid only gets productId off the search
  // row (see Contact in models/contacts.ts), so the name it needs for the Type column's icon has
  // to be resolved against the same cached lookups the department dialogs already use.
  private productsById = new Map<number, string>();
  private allContacts: Contact[] = [];
  private activeFilter: ContactsFilter = { search: null, type: null };
  private sortState: Sort = { active: '', direction: '' };
  private viewport: CdkVirtualScrollViewport | null = null;

  ngOnInit() {
    this.isUserAdmin$ = this.userService.isUserAdmin();
    this.loadContacts();
    this.lookupsService
      .getLookups()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(lookups => {
        this.productsById = new Map(lookups.products.map(product => [product.id, product.name]));
      });
    this.organisationsService
      .count()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(count => (this.organisationsCount = count));
  }

  private loadContacts() {
    this.isLoading = true;
    this.hasError = false;
    this.contactsService
      .getAllContacts()
      .pipe(
        catchError(() => {
          this.hasError = true;
          return of<ContactsAllResponse>({ data: [] });
        }),
        tap(response => {
          // The grid holds every contact in memory and filters client-side, so a response that
          // came back short of totalCount means rows are missing from the grid entirely — not
          // merely off the current page, since there is no next page to scroll to. That is how
          // the server's default page size of 20 went unnoticed once it started applying again.
          if (response.totalCount !== undefined && response.data.length < response.totalCount) {
            console.warn(
              `Contacts grid: server returned ${response.data.length} of ${response.totalCount} contacts — ` +
                `the list is truncated. The search body should be asking for the unpaged set ` +
                `(pageSize: null in ContactsService.getAllContacts).`
            );
          }
          this.allContacts = response.data;
          this.allContactsCount = response.data.length;
          this.applyFilterAndSort();
          this.isTableInitiating = false;
          this.isLoading = false;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  // Search and type filtering run entirely client-side against the full contact set fetched
  // once in ngOnInit (contacts per organization are small — a handful, at most low tens — so
  // holding all of them in memory and filtering locally is effectively free, same reasoning
  // that already made sorting client-side below). No server round-trip per filter change.
  onFilterChange(filter: { search?: { columnName: string | null; value: string }; type?: ContactType | 'All' } | null) {
    if (filter?.search) {
      this.activeFilter = { ...this.activeFilter, search: filter.search };
    } else if (filter?.type) {
      this.activeFilter = { ...this.activeFilter, type: filter.type === 'All' ? null : filter.type };
    } else {
      this.activeFilter = {
        search: filter?.search ?? null,
        type: filter?.type === 'All' ? null : (filter?.type ?? null)
      };
      this.sortState = { active: '', direction: '' };
      this.matSort.direction = '';
      this.matSort.active = '';
    }
    this.applyFilterAndSort();
  }

  // Avatars in this grid are coloured by contact type. An unrecognised or missing type falls
  // back to the person pair, which is also the avatar component default.
  avatarColor(type: ContactType): string {
    return (AVATAR_COLORS_BY_TYPE[type] ?? AVATAR_COLORS_BY_TYPE.Person).background;
  }

  avatarTextColor(type: ContactType): string {
    return (AVATAR_COLORS_BY_TYPE[type] ?? AVATAR_COLORS_BY_TYPE.Person).text;
  }

  // Swaps the Name column's avatar for the Product's own icon — null for a Person row, or a
  // Department with no Product picked yet, which keeps the existing initials/photo avatar.
  productName(contact: Contact): string | null {
    if (contact.type !== 'Department' || contact.productId === null) {
      return null;
    }
    return this.productsById.get(contact.productId) ?? null;
  }

  onSort(sort: Sort) {
    this.sortState = sort;
    this.applyFilterAndSort();
  }

  // The header row lives outside the CDK viewport (see the template comment for why), so it
  // doesn't get horizontal scroll for free — mirror the viewport's scrollLeft onto it here.
  onViewportScroll(event: Event) {
    this.headerRow.nativeElement.scrollLeft = (event.target as HTMLElement).scrollLeft;
  }

  createContact(type: ContactType) {
    const dialogRef =
      type === 'Person'
        ? this.matDialog.open(AddPersonContactComponent, CONTACT_DIALOG_CHROME)
        : this.matDialog.open(AddDepartmentContactComponent, {
            ...CONTACT_DIALOG_CHROME,
            // The dialog warns on a name that is already taken; it reuses the contact set this
            // grid has already fetched rather than making its own request for it.
            data: { existingNames: this.allContacts.map(contact => contact.displayName) }
          });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        // AddPersonContactComponent still closes with the bare string; AddDepartmentContactComponent
        // closes with { action: 'Added' | 'Updated', ... } — see AddDepartmentContactResult. A
        // brand-new row needs its server-assigned id/photoUrl/etc., so it still gets a full refetch
        // rather than the in-place patch editContact() below uses for an existing row.
        if (result === 'Added' || result?.action === 'Added') {
          this.loadContacts();
        }
      });
  }

  // Guarded by `filter(result === 'Confirmed')` so dismissing the dialog (Cancel or backdrop)
  // is a no-op — without it, afterClosed() emits on any close and would delete regardless.
  deleteContact({ id, displayName }: Contact) {
    openConfirmationDialog(this.matDialog, {
      title: 'Delete contact',
      message: `Are you sure you want to delete contact: ${displayName}?`
    })
      .afterClosed()
      .pipe(
        filter(result => result === 'Confirmed'),
        // The confirmation dialog is already closed by the time this runs (onConfirm() closes it
        // immediately), so the in-flight spinner has to live on the page itself, not the dialog.
        tap(() => (this.isLoading = true)),
        switchMap(() => this.contactsService.delete(id)),
        tap(() => {
          // The grid is fed from one up-front fetch of every contact (see loadContacts), so
          // there is no page to re-request — refetch the whole set to drop the deleted row.
          this.loadContacts();
          this.matSnackBar.open('The contact was deleted successfully', 'x', { panelClass: 'snackbar-green' });
        }),
        catchError(error => {
          console.error(error);
          this.isLoading = false;
          this.matSnackBar.open('Could not delete the contact, please try again', 'x');

          return NEVER;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  editContact(contact: Contact) {
    if (contact.type === 'Person') {
      // Nothing this dialog can change (visibility toggles, assignments, isActive — which just
      // follows Active Directory) is reflected in a grid column, so there is no row to patch and
      // nothing to refetch: a loadContacts() here would only flash the spinner and reset scroll
      // for a save with no visible effect on the grid.
      this.matDialog.open<EditContactComponent, EditContactDialogData>(EditContactComponent, {
        ...CONTACT_DIALOG_CHROME,
        data: { id: contact.id, photoUrl: contact.photoUrl }
      });
      return;
    }

    // Department: reuse the Add Department dialog, seeded with the row already fetched from
    // /supportContacts/search (it now carries productId/functionId/branchOfficeId/description —
    // see Contact in models/contacts.ts) rather than making a dedicated request for them.
    const dialogRef = this.matDialog.open<
      AddDepartmentContactComponent,
      AddDepartmentContactData,
      AddDepartmentContactResult
    >(AddDepartmentContactComponent, {
      ...CONTACT_DIALOG_CHROME,
      data: {
        existingNames: this.allContacts.filter(c => c.id !== contact.id).map(c => c.displayName),
        editContact: contact
      }
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        // An edit can only change fields already known client-side (the dialog built them from
        // the same form it just submitted), so patch the one row in place instead of refetching
        // and re-rendering the whole grid — loadContacts() would flash the spinner and reset
        // scroll to the top, which is disruptive when editing mid-scroll in a long list.
        if (result?.action === 'Updated') {
          this.patchContact(result.contact.id, result.contact);
        }
      });
  }

  // See the comment on editContact()'s Department branch — avoids the full-grid reload's
  // spinner flash and scroll reset for a save whose effect is already known client-side.
  private patchContact(id: number, patch: Partial<Contact>) {
    const index = this.allContacts.findIndex(c => c.id === id);
    if (index === -1) {
      return;
    }
    this.allContacts = [
      ...this.allContacts.slice(0, index),
      { ...this.allContacts[index], ...patch },
      ...this.allContacts.slice(index + 1)
    ];
    this.applyFilterAndSort(false);
  }

  // resetScroll is false only for patchContact() above — a full loadContacts() (add/delete/type
  // filter/search/sort change) legitimately changes what's visible and should scroll back to the
  // top; an in-place edit of one already-visible row should leave the admin's scroll position alone.
  private applyFilterAndSort(resetScroll = true) {
    const filtered = this.allContacts.filter(contact => matchesContactFilter(contact, this.activeFilter));
    const { active, direction } = this.sortState;
    const sorted = direction
      ? [...filtered].sort((a, b) => {
          const cmp = displayNameCollator.compare(
            String(a[active as keyof Contact] ?? ''),
            String(b[active as keyof Contact] ?? '')
          );
          return direction === 'asc' ? cmp : -cmp;
        })
      : filtered;
    this.totalCount = filtered.length;
    this.dataSource.setData(sorted);
    if (resetScroll) {
      this.viewport?.scrollToOffset(0);
    }
  }
}
